import { useCurrentPortfolioStore } from '../../vue/stores/currentPortfolioStore'
import { useTranslationsStore } from '../../vue/stores/translationsStore'

const FIELD_UNIT_MAP = {
    'gross_floor_area_sqm': {
        metric: { units: ['m2'] },
        imperial: { units: ['ft2'] },
    },
    'energy_intensity_kwhsqm': {
        metric: { units: ['kwh', 'm2'], label: 'general.units.units-per-year' },
        imperial: { units: ['kbtu', 'ft2'], label: 'general.units.units-per-year' },
    },
    'energy_consumption_kwh/1000': {
        metric: { units: ['mwh'] },
        imperial: { units: ['mmbtu'] },
    },
}

function labelForField(field) {
    const { translate } = useTranslationsStore()
    const currentPortfolioStore = useCurrentPortfolioStore()
    const unitSystem = currentPortfolioStore.data?.default_unit === 'imperial' ? 'imperial' : 'metric'

    const config = FIELD_UNIT_MAP[field]
    if (!config) {
        return ''
    }

    const unitConfig = config[unitSystem]
    const translatedUnits = unitConfig.units.map(u => translate(`general.units.${u}`))

    if (unitConfig.label) {
        const params = {}
        translatedUnits.forEach((unit, i) => {
            params[`unit${i + 1}`] = unit
        })
        return translate(unitConfig.label, params)
    }

    return translatedUnits[0]
}

/**
 * Custom tooltip formatter for asset info charts.
 * Extracted from app.blade.php to work with the separated frontend.
 * Must be called with .bind(chartInstance) so `this` refers to the ECharts instance.
 */
export function customAssetInfoTooltipFormatter(params) {
    const { translate } = useTranslationsStore()

    params = Array.isArray(params) ? params : [params]
    const chartOptions = this.getOption()
    // This handles case when the first series is disabled by clicking on the histogram legend
    const asset = Object.keys(params[0]?.data?.tooltipData?.assetData ?? {}).length
        ? params[0].data.tooltipData.assetData
        : chartOptions.series[0]['data'][params[0].dataIndex]?.tooltipData?.assetData
    if (!asset) {
        return
    }
    let colorCircles = ''
    let itemValues = ''
    params.forEach((param) => {
        param.data.tooltipData.valueData.forEach((item) => {
            if (item.color) {
                colorCircles += `<div style="height: 12px; width: 12px; border-radius: 12px; background: #FFFFFF; margin: 5px 5px 5px 0;">
                        <div style="height: 12px; width: 12px; border-radius: 12px; background:${item.color}; margin: 5px 5px 5px 0;"></div>
                    </div>`
            }
            itemValues += `
                <tr>
                    <td style="color: rgba(176, 176, 176, 1);">${item.title}:</td>
                    <td>${item.value}</td>
                </tr>
            `
        })
    })
    // Echarts does not trigger mouseleave event when the user moves out of the custom tooltip without moving the mouse over the chart after that.
    // So, this code is used to trigger event that will remove highlight of the currently focused item.
    setTimeout(() => {
        const tooltipContainer = this.getDom().getElementsByClassName('js-asset-info-tooltip')[0]
        if (tooltipContainer && tooltipContainer.getAttribute('listener') !== 'true') {
            tooltipContainer.setAttribute('listener', 'true')
            tooltipContainer.addEventListener('mouseleave', () => {
                this.getDom().firstChild.dispatchEvent(new Event('mouseout'))
            })
        }
    }, 0)

    let simpleLayout = false
    let simpleLayoutV2 = false
    chartOptions.series.forEach((el) => {
        if (el.tooltip?.customTooltip?.showBasicInfo) {
            simpleLayout = true
        }
        if (el.tooltip?.customTooltip?.showBasicInfoV2) {
            simpleLayoutV2 = true
        }
    })
    if (simpleLayoutV2) {
        let total = null
        let hasEstimated = false
        chartOptions.series.forEach((serie) => {
            if (serie.name?.includes('Estimated')) {
                hasEstimated = true
            }
        })
        const hasMultipleSeries = chartOptions.series.filter(el => Boolean(el.data.length)).length > 2
        const hideTotal = chartOptions.customTooltip?.hideTotal
        const showOwnedForEntireYearWarning = asset.owned_for_entire_interval_period_bool !== undefined && asset.owned_for_entire_interval_period_bool === false && asset.owned_since
        let tooltipContent = ''
        if (chartOptions?.series?.[0]?.data?.[params[0].dataIndex]?.tooltipData?.portfolioData?.name) {
            tooltipContent += `
                <div style="display:flex; align-items: center; justify-content: between; gap: 8px; font-size: 14px; font-weight: bold; margin-bottom: 6px; color:#727C8F;">
                 <span>${translate('general.portfolio')}:</span>
                 <span>${chartOptions.series[0].data[params[0].dataIndex].tooltipData.portfolioData.name}</span>
                </div>
            `
        }
        tooltipContent += `<span style="font-weight:bold;color:#727C8F;font-size:14px; min-width: 193px;">${asset.name}</span><br><span style="font-size:10px;font-weight:bold;color:#99A4B1;">${(hasMultipleSeries ? chartOptions.series[0].tooltip.customTooltip.unit ?? '' : '')}</span>${(!showOwnedForEntireYearWarning && hasMultipleSeries && chartOptions.series[0].tooltip.customTooltip.unit ? '<br>' : '')}`
        tooltipContent += chartOptions.series[0].tooltip.customTooltip?.showCrremPropertyType ? `<span style="display: block;font-weight:bold;font-size:12px; margin-bottom:4px; min-width: 193px;">${asset.crrem_property_type_name}</span>` : (!showOwnedForEntireYearWarning ? '<br>' : '')
        if (showOwnedForEntireYearWarning) {
            const message = asset.owned_until
                ? asset.months_owned + ' ' + translate('general.charts.tooltip.asset-not-owned-for-entire-year-until-part-2') + asset.owned_until
                : asset.months_owned + ' ' + translate('general.charts.tooltip.asset-not-owned-for-entire-year-since-part-2') + asset.owned_since
            tooltipContent += `
                <div class="mt-1 ${hasMultipleSeries ? 'mb-3' : 'mb-2'}">
                    <div class="flex gap-x-1 items-center w-full">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8" cy="8" r="6" transform="rotate(-180 8 8)" stroke="#FB923C"/>
                            <path d="M8 10V9.999" stroke="#FB923C" stroke-width="1.33333" stroke-linecap="round"/>
                            <line x1="8.00002" y1="7.99984" x2="8.00002" y2="5.33317" stroke="#FB923C" stroke-width="1.33333" stroke-linecap="round"/>
                        </svg>
                        <p class="text-xs word-wrap text-scaler-gray-dark font-medium">${translate('general.charts.tooltip.asset-not-owned-for-entire-year-part-1')} ${message}</p>
                    </div>
                </div>
            `
        }
        const showEstimatedPercentage = chartOptions.series[0].tooltip.customTooltip?.showEstimatedPercentage
        if (hasMultipleSeries) {
            chartOptions.series.forEach((serie, index) => {
                const isEstimated = serie.name.includes('Estimated')
                if (serie.name !== translate('general.charts.titles.missing-assets') && serie.data[params[0].dataIndex] && !isEstimated) {
                    const estimatedSerie = chartOptions.series.filter(
                        el => el.name === `Estimated ${serie.name}`,
                    )[0]
                    if (serie.data[params[0].dataIndex]?.value !== null && serie.data[params[0].dataIndex]?.value !== undefined) {
                        total += parseFloat(serie.data[params[0].dataIndex].value)
                    }
                    const currentItemValue = serie.data[params[0].dataIndex].value
                    const estimatedSerieValue = hasEstimated ? estimatedSerie?.data[params[0].dataIndex]?.value : null
                    let itemValue = estimatedSerieValue
                        ? parseFloat(estimatedSerieValue) + parseFloat(currentItemValue ?? 0)
                        : currentItemValue
                    if (currentItemValue && estimatedSerieValue) {
                        itemValue = Math.round(itemValue * 100) / 100
                    }
                    if (serie.tooltip?.hide) {
                        return
                    }
                    if (serie.data[params[0].dataIndex].customValue) {
                        itemValue = serie.data[params[0].dataIndex].customValue
                    }
                    tooltipContent += `
                        <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%; margin-top: ${hasEstimated ? '0px' : '-6px'};">
                            <div style="height: 24px; width: 3px; background:${hasEstimated ? chartOptions.color[index] : chartOptions.color[index]}; margin-right: 10px; ${(index == 0 || (hasEstimated) ? 'border-top-left-radius: 10px;border-top-right-radius: 10px;' : '')};  ${((index + 1 && !hasEstimated) || (hasEstimated && !estimatedSerie) == chartOptions.series.length ? 'border-bottom-left-radius: 10px;border-bottom-right-radius: 10px;' : '')}"></div>
                            <span style="color: #111827;font-weight:bold;font-size:14px;margin-right: 16px;">${(hasMultipleSeries ? (isEstimated ? 'Estimated data' : (serie.customTooltipLabel || serie.name)) : serie.data[params[0].dataIndex].value ?? '-')}</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${(hasMultipleSeries ? itemValue ?? '-' : (chartOptions.series[0].tooltip.customTooltip.unit ?? ''))}</span>
                        </div><br>
                    `
                    if (estimatedSerie) {
                        const estimatedCurrentItemValue = estimatedSerie.data[params[0].dataIndex].value
                        if (estimatedCurrentItemValue !== null && estimatedCurrentItemValue !== undefined) {
                            total += parseFloat(estimatedCurrentItemValue)
                        }
                        const nextSeriesItemValue = serie.data[params[0].dataIndex]?.value ?? 0
                        let itemValue = showEstimatedPercentage && nextSeriesItemValue && estimatedCurrentItemValue !== null
                            ? (estimatedCurrentItemValue / (parseFloat(estimatedCurrentItemValue) + parseFloat(nextSeriesItemValue))) * 100
                            : estimatedCurrentItemValue
                        if (showEstimatedPercentage) {
                            itemValue = !nextSeriesItemValue && estimatedCurrentItemValue ? 100 : Math.round(itemValue * 100) / 100
                        }
                        tooltipContent += `
                            <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%; margin-top: -8px;">
                                <div style="height: 24px; width: 3px; background:${chartOptions.color[index]}; margin-right: 10px; border-bottom-left-radius: 10px;border-bottom-right-radius: 10px;"></div>
                                <span style="color: #111827;font-weight:bold;font-size:14px;margin-right: 16px;">${showEstimatedPercentage ? 'Estimated data' : 'Estimated ' + (serie.customTooltipLabel ?? serie.name).toLowerCase()}</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${itemValue ?? '-'} <span style="font-weight: 500;">${showEstimatedPercentage ? '%' : ''}</span></span>
                            </div><br>
                        `
                        if (!showEstimatedPercentage) {
                            tooltipContent += `
                            <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%; margin-top: -6px;">
                                    <div style="height: 22px; width: 3px; background:${chartOptions.color[index]}; margin-right: 10px; border-bottom-left-radius: 10px;border-bottom-right-radius: 10px;"></div>
                                    <span style="color: #111827;font-weight:bold;font-size:14px;margin-right: 16px;">${'Actual ' + (serie.customTooltipLabel ?? serie.name).toLowerCase()}</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${currentItemValue ?? '-'}</span>
                                </div><br>
                            `
                        }
                    }
                }
            })
        } else {
            chartOptions.series[0].data[params[0].dataIndex].tooltipData.valueData.forEach((item) => {
                const value = item.value.split(' ')[0]
                const unit = item.value.split(' ')[1] ?? ''
                tooltipContent += `
                    <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;">
                        <div style="height: 24px; width: 3px; background:${chartOptions.series[0].data[params[0].dataIndex].tooltipData.valueData[0].color}; margin-right: 10px; border-top-left-radius: 10px;border-top-right-radius: 10px;border-bottom-left-radius: 10px;border-bottom-right-radius: 10px;"></div>
                        <span style="color: #111827;font-weight:bold;font-size:14px;margin-right: 16px;">${item.title}</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${value} <span style="font-weight: 500;">${unit}</span></span>
                    </div><br>
                `
            })
        }

        if (chartOptions.series[0].tooltip.customTooltip.dataCoverageChartId) {
            let assetDataFromSeries = null
            chartOptions.series.forEach((serie) => {
                if (serie.data[params[0].dataIndex]?.tooltipData?.assetData) {
                    assetDataFromSeries = serie.data[params[0].dataIndex]?.tooltipData?.assetData
                }
            })
            const dataCoverageValue = assetDataFromSeries?.energy_data_coverage_perc.split(' ')[0]
            const dataCoverageUnit = assetDataFromSeries?.energy_data_coverage_perc.split(' ')[1] ?? ''
            tooltipContent += `
                <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;margin-top: -8px;">
                    <div style="height: 24px; width: 3px; background:${chartOptions.color[0]}; margin-right: 10px;border-bottom-left-radius: 10px;border-bottom-right-radius: 10px;"></div>
                    <span style="color: #505867;font-weight:bold;font-size:12px;margin-right: 16px;">${hasEstimated ? 'Data coverage excluding estimations' : 'Data coverage'}</span><span style="font-size:12px;font-weight:bold;color:#505867;margin-left:auto;">${dataCoverageValue} <span style="font-weight: 500;">${dataCoverageUnit}</span></span>
                </div><br>
            `
        }

        if (chartOptions.tooltipAdditionalFields) {
            chartOptions.tooltipAdditionalFields.forEach((field) => {
                tooltipContent += `
                    <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;margin-top: -8px;">
                        <div style="height: 24px; width: 3px; background:${chartOptions.color[0]}; margin-right: 10px;border-bottom-left-radius: 10px;border-bottom-right-radius: 10px;"></div>
                        <span style="color: #505867;font-weight:bold;font-size:12px;margin-right: 16px;">${field.label}</span><span style="font-size:12px;font-weight:bold;color:#505867;margin-left:auto;">${(asset[field.name] !== null && asset[field.name] !== undefined && asset[field.name] !== '' ? asset[field.name] : '-')} <span style="font-weight: 500;">${field.unit}</span></span>
                    </div><br>
                `
            })
        }

        if (hasMultipleSeries && !hideTotal) {
            tooltipContent += `
                <div style="display:inline-flex; padding-left:13px; align-items: center; justify-content: between; width: 100%;margin-top: ${hasEstimated ? '0' : '-8px'};">
                    <span style="color: #111827;font-weight:bold;font-size:14px;margin-right: 16px;">Total</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${total !== null ? Math.round(total * 100) / 100 : '-'}</span>
                </div><br>
            `
        }
        return tooltipContent
    }
    if (simpleLayout) {
        return `<div>
            <div
            class="js-asset-info-tooltip"
            style="
                background: #111827;
                border-radius: 4px;
                color: rgba(255, 255, 255, 1);
                font-size: 11px;
                font-weight: 500;
                padding: 6px 12px 6px 12px;
                margin: -3px -3px -3px -3px;
            ">
                <p>
                    ${asset.name} <span style="color: #CA8A04; font-weight:700; margin-left: 4px;">${params[0]?.data?.tooltipData?.valueData[0]?.value}</span>
                </p>
                </div>
                <svg width="22" style="margin-bottom: -24px; margin-left: auto; margin-right: auto;" height="12" viewBox="0 0 22 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 12L0.607697 0L21.3923 0L11 12Z" fill="#111827"/>
                </svg>
            </div>
        `
    }
    let div = `
        <div
        class="js-asset-info-tooltip"
        style="
            background: rgba(0, 0, 0, 0.75);
            border-radius: 10px;
            color: rgba(255, 255, 255, 1);
            font-size: 11px;
            font-weight: bold;
            padding: 6px 6px 6px 15px;
            min-width: 247px;
            margin: -3px -3px -3px -3px;
        ">
            <p>${asset.name} - ${asset.external_id}</p>
            <div style="display:inline-flex; align-items: center;">
                <div>
                    ${colorCircles}
                </div>
                <div>
                    <table style="font-size: 10px;line-height: 14px;">
                        ${itemValues}
                    </table>
                </div>
            </div>
            <div style="width: 190px; height: 1px; background: #FFFFFF; margin: 4px 0 6px 0;"></div>
            <table style="font-size: 10px;line-height: 14px;">`
    if (asset.property_type_name) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.charts.tooltip.property-type')}</td>
                    <td>${asset.property_type_name}</td>
                </tr>`
    }
    if (asset.property_subtype_name) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.charts.tooltip.property-subtype')}</td>
                    <td>${asset.property_subtype_name}</td>
                </tr>`
    }
    if (asset.gross_floor_area_sqm) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.charts.tooltip.gross-floor-area')}</td>
                    <td>${asset.gross_floor_area_sqm} ${labelForField('gross_floor_area_sqm')}</td>
                </tr>`
    }
    if (asset.energy_intensity_kwhsqm) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.charts.tooltip.energy-use-intensity')}</td>
                    <td>${asset.energy_intensity_kwhsqm} ${labelForField('energy_intensity_kwhsqm')}</td>
                </tr>`
    }
    if (asset.energy_consumption_mwh) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.charts.tooltip.total-consumption')}</td>
                    <td>${asset.energy_consumption_mwh} ${labelForField('energy_consumption_kwh/1000')}</td>
                </tr>`
    }
    if (asset.energy_data_coverage_perc) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.energy-data-coverage')}</td>
                    <td>${asset.energy_data_coverage_perc} ${translate('%')}</td>
                </tr>`
    }
    if (asset.energy_fuels_data_coverage_perc) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.charts.tooltip.fuels-data-coverage')}</td>
                    <td>${asset.energy_fuels_data_coverage_perc} ${translate('%')}</td>
                </tr>`
    }
    if (asset.energy_dhc_data_coverage_perc) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.charts.tooltip.dhc-data-coverage')}</td>
                    <td>${asset.energy_dhc_data_coverage_perc} ${translate('%')}</td>
                </tr>`
    }
    if (asset.energy_electricity_data_coverage_perc) {
        div += `<tr>
                    <td style="color: rgba(176, 176, 176, 1);">${translate('general.charts.tooltip.electricity-data-coverage')}</td>
                    <td>${asset.energy_electricity_data_coverage_perc} ${translate('%')}</td>
                </tr>`
    }

    div += '</table>'

    if (asset.details_url) {
        div += `<div style="width: 100%; text-align: right; padding-right: 5px;">
            <a style="  color: rgba(34, 149, 255, 1); font-weight: bold;" href="${asset.details_url}">
                ${translate('general.charts.tooltip.more-details')}
            </a>
        </div>`
    }

    return div
}
